import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  InputGroup,
  Table,
  Pagination,
  OverlayTrigger,
  Tooltip,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectTokens } from "../redux/userSlice";
import LoadingAnimation from "./LoadingAnimation";
import "./UserManagementComponent.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faEdit,
  faSortUp,
  faSortDown,
  faSpinner,
  faPaperPlane,
  faRotateRight,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import {
  downloadTaxSaleBidderRegistrationCard,
  emailTaxSaleBidderRegistrationCard,
} from "../redux/registrationSlice";
import { useDispatch } from "react-redux";
import UserEditModal from "./UserManagementEditUserModal";

const UserManagementComponent = ({
  setToastHeader,
  setToastBody,
  setShowToast,
}) => {
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const usersPerPage = 10;
  const [downloadLoading, setDownloadLoading] = useState({});
  const [emailLoading, setEmailLoading] = useState({});
  const [syncingLoading, setSyncingLoading] = useState(false);
  const [showUsersLegacyReg, setShowUsersLegacyReg] = useState([]);

  const tokens = useSelector(selectTokens);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const apiKey = process.env.REACT_APP_SERVER_API_KEY;

  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortField, sortAsc]);

  const fetchAllUsers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/user/all`, {
        // const response = await axios.get(`http://localhost:8080/user/all`, {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          "x-api-key": apiKey,
        },
      });

      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const syncUsers = async () => {
    setSyncingLoading(true);
    try {
      await axios.post(
        `${baseUrl}/app/api/sync-fusionauth-users`,
        {},
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            "x-api-key": apiKey,
          },
        }
      );
      setShowToast(true);
      setToastHeader("Syncing Users");
      setToastBody("Successfully Synced Users!");
    } catch (error) {
      console.error("Error syncing users:", error);
      setShowToast(true);
      setToastHeader("Syncing Users");
      setToastBody("Error Syncing Users");
    } finally {
      setSyncingLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleUserDetailChange = (e, addressIndex = null, key = null) => {
    const { name, value } = e.target;
    if (addressIndex !== null && key) {
      setEditingUser((prev) => ({
        ...prev,
        addresses: prev.addresses.map((addr, idx) =>
          idx === addressIndex ? { ...addr, [key]: value } : addr
        ),
      }));
    } else {
      setEditingUser((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleOrganizationDetailChange = (
    e,
    orgIndex,
    addrIndex = null,
    key = null
  ) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({
      ...prev,
      organizations: prev.organizations.map((org, oIdx) => {
        if (oIdx !== orgIndex) return org;
        if (addrIndex !== null && key) {
          return {
            ...org,
            addresses: org.addresses.map((addr, aIdx) =>
              aIdx === addrIndex ? { ...addr, [key]: value } : addr
            ),
          };
        } else {
          return {
            ...org,
            [name]: value,
          };
        }
      }),
    }));
  };

  const handleSave = async () => {
    try {
      const { user, organizations } = editingUser;
      const orgData = organizations?.map((org) => {
        const orgCopy = { ...org };
        if (!orgCopy.addresses || orgCopy.addresses.length === 0) {
          orgCopy.addresses = [
            {
              street_address: "",
              street_address2: "",
              city: "",
              state: "",
              zip: "",
            },
          ];
        }
        return orgCopy;
      });

      const payload = {
        userData: {
          ...user,
          addresses: undefined,
        },
        userAddress: user?.addresses?.[0] || {
          street_address: "",
          street_address2: "",
          city: "",
          state: "",
          zip: "",
        },
        orgData: organizations?.map(({ addresses, ...rest }) => rest),
        orgAddress: orgData[0]?.addresses[0] || {
          street_address: "",
          street_address2: "",
          city: "",
          state: "",
          zip: "",
        },
      };

      await axios.put(`${baseUrl}/users/${user.fusionauth_user_id}`, payload, {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          "x-api-key": apiKey,
          "Content-Type": "application/json",
        },
      });

      setShowEditModal(false);
      fetchAllUsers();
    } catch (err) {
      console.error("Error saving user changes:", err);
      alert("Failed to save changes.");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  //NOTE: this doesn't search on legacy reg numbers.
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();

    const fullName = `${user.first_name || ""} ${
      user.last_name || ""
    }`.toLowerCase();
    const globalRegNumber = user.global_reg_number?.toString() || "";

    return (
      fullName.includes(search) ||
      globalRegNumber.includes(search) ||
      Object.values(user).some(
        (val) => typeof val === "string" && val.toLowerCase().includes(search)
      )
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortField) return 0;
    const valA = a[sortField]?.toLowerCase?.() || "";
    const valB = b[sortField]?.toLowerCase?.() || "";
    return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDownloadRegistrationCardPDF = async (user) => {
    setDownloadLoading((prev) => ({
      ...prev,
      [`${user.fusionauth_user_id}`]: true,
    }));
    try {
      await dispatch(
        downloadTaxSaleBidderRegistrationCard({ selectedUser: user })
      ).unwrap();
    } catch (error) {
      console.error(
        "Error retrieving tax sale bidder registration document:",
        error
      );
      alert("Failed to retrieve Tax Sale Bidder Registration Document.");
    } finally {
      setDownloadLoading((prev) => ({
        ...prev,
        [`${user.fusionauth_user_id}`]: false,
      }));
    }
  };

  const DownloadRegistrationDocumentTooltip = ({ reg }) => {
    const loadingKey = `${reg.fusionauth_user_id}`;
    const isLoading = downloadLoading[loadingKey] || false;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="tooltip-top">
            Download Tax Sale Bidder Registration Card
          </Tooltip>
        }
      >
        <Button
          variant="outline-secondary"
          disabled={isLoading}
          className="w-100 mb-2"
          onClick={() => handleDownloadRegistrationCardPDF(reg)}
        >
          <>
            {!isLoading ? (
              <FontAwesomeIcon icon={faFile} />
            ) : (
              <FontAwesomeIcon icon={faSpinner} spin />
            )}
          </>
        </Button>
      </OverlayTrigger>
    );
  };

  const handleEmailRegistrationCardPDF = async (user) => {
    setEmailLoading((prev) => ({
      ...prev,
      [`${user.fusionauth_user_id}`]: true,
    }));
    try {
      await dispatch(
        emailTaxSaleBidderRegistrationCard({ selectedUser: user })
      ).unwrap();
      setShowToast(true);
      setToastHeader("Tax Bidder Registration Card Email");
      setToastBody("Successfully sent email!");
    } catch (error) {
      console.error(
        "Error emailing tax sale bidder registration document:",
        error
      );
      alert("Failed to email Tax Sale Bidder Registration Document.");
    } finally {
      setEmailLoading((prev) => ({
        ...prev,
        [`${user.fusionauth_user_id}`]: false,
      }));
    }
  };

  const EmailRegistrationDocumentTooltip = ({ reg }) => {
    const loadingKey = `${reg.fusionauth_user_id}`;
    const isLoading = emailLoading[loadingKey] || false;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="tooltip-top">
            Email Tax Sale Bidder Registration Card
          </Tooltip>
        }
      >
        <Button
          variant="outline-secondary"
          disabled={isLoading}
          className="w-100 mb-2"
          onClick={() => handleEmailRegistrationCardPDF(reg)}
        >
          <>
            {!isLoading ? (
              <FontAwesomeIcon icon={faPaperPlane} />
            ) : (
              <FontAwesomeIcon icon={faSpinner} spin />
            )}
          </>
        </Button>
      </OverlayTrigger>
    );
  };

  const renderSortIcon = (field) => {
    const colorUp = sortField === field && sortAsc ? "#F16B3F" : "#ccc";
    const colorDown = sortField === field && !sortAsc ? "#F16B3F" : "#ccc";
    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          verticalAlign: "middle",
          marginLeft: "6px",
        }}
      >
        <FontAwesomeIcon
          icon={faSortUp}
          style={{ color: colorUp, height: "12px" }}
        />
        <FontAwesomeIcon
          icon={faSortDown}
          style={{ color: colorDown, height: "12px" }}
        />
      </span>
    );
  };

  return (
    <div className="user-management-container" style={{ padding: "1rem" }}>
      <h2>User Management</h2>

      <div className="d-flex">
        <Row className="w-100 flex-grow-1 align-items-baseline">
          <Col className="w-75">
            <InputGroup>
              <Form.Control
                placeholder="Search by any field..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col className="w-10 text-end">
            <Button
              className="m-0 btn-light d-flex align-items-center gap-2"
              style={{ justifySelf: "flex-end" }}
              onClick={() => syncUsers()}
            >
              Sync Users{" "}
              {syncingLoading ? (
                <FontAwesomeIcon spin icon={faRotate} />
              ) : (
                <FontAwesomeIcon icon={faRotate} />
              )}
            </Button>
          </Col>
        </Row>
      </div>

      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <>
          <Table striped bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th onClick={() => handleSort("fusionauth_user_id")}>
                  User ID {renderSortIcon("fusionauth_user_id")}
                </th>
                <th>Registration Number</th>
                <th onClick={() => handleSort("first_name")}>
                  Name {renderSortIcon("first_name")}
                </th>
                <th onClick={() => handleSort("email")}>
                  Email {renderSortIcon("email")}
                </th>
                <th onClick={() => handleSort("phone")}>
                  Phone {renderSortIcon("phone")}
                </th>
                <th>Registration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.fusionauth_user_id}>
                  <td>
                    <small>{user.fusionauth_user_id}</small>
                  </td>
                  <td>
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em",
                        whiteSpace: "nowrap",
                        margin: 0,
                      }}
                    >
                      <small
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em",
                        }}
                      >
                        <Badge bg="secondary">Global</Badge>
                        {user.global_reg_number}
                      </small>
                    </p>

                    {user.legacy_registrations?.length > 0 && (
                      <p>
                        <small>
                          <button
                            style={{
                              border: "none",
                              backgroundColor: "transparent",
                              padding: 0,
                              lineHeight: 1,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setShowUsersLegacyReg((arr) => {
                                if (arr.includes(user.fusionauth_user_id)) {
                                  return arr.filter(
                                    (id) => id !== user.fusionauth_user_id
                                  );
                                } else {
                                  return [...arr, user.fusionauth_user_id];
                                }
                              })
                            }
                          >
                            {" "}
                            <Badge bg="secondary">
                              {showUsersLegacyReg.includes(
                                user.fusionauth_user_id
                              )
                                ? "Hide Legacy"
                                : "Show Legacy"}
                            </Badge>{" "}
                          </button>
                          <br />
                          {showUsersLegacyReg.includes(
                            user.fusionauth_user_id
                          ) &&
                            [
                              ...new Set(JSON.parse(user.legacy_registrations)),
                            ].join(", ")}
                        </small>
                      </p>
                    )}
                  </td>
                  <td>
                    {user.first_name} {user.last_name}
                  </td>
                  <td>{user.email}</td>
                  <td>{user.phone}</td>
                  <td>
                    <div class="d-flex justify-even">
                      <DownloadRegistrationDocumentTooltip reg={user} />
                      <EmailRegistrationDocumentTooltip reg={user} />
                    </div>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleEditClick(user)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {sortedUsers.length > usersPerPage && (
            <div className="text-center mt-3">
              <Pagination className="d-inline-flex">
                <Pagination.First
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                />

                {(() => {
                  const totalPages = Math.ceil(
                    sortedUsers.length / usersPerPage
                  );
                  const pages = [];

                  if (totalPages <= 10) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    if (currentPage <= 4) {
                      pages.push(1, 2, 3, 4, 5, "...", totalPages);
                    } else if (currentPage >= totalPages - 2) {
                      pages.push(
                        1,
                        "...",
                        totalPages - 4,
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages
                      );
                    } else {
                      pages.push(
                        1,
                        "...",
                        currentPage - 2,
                        currentPage - 1,
                        currentPage,
                        currentPage + 1,
                        currentPage + 2,
                        "...",
                        totalPages
                      );
                    }
                  }

                  return pages.map((page, index) =>
                    page === "..." ? (
                      <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                    ) : (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => paginate(page)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  );
                })()}

                <Pagination.Next
                  onClick={() => paginate(currentPage + 1)}
                  disabled={
                    currentPage === Math.ceil(sortedUsers.length / usersPerPage)
                  }
                />
                <Pagination.Last
                  onClick={() =>
                    paginate(Math.ceil(sortedUsers.length / usersPerPage))
                  }
                  disabled={
                    currentPage === Math.ceil(sortedUsers.length / usersPerPage)
                  }
                />
              </Pagination>
            </div>
          )}
          {sortedUsers.length > 0 && (
            <p style={{ textAlign: "center" }}>
              {(currentPage - 1) * usersPerPage + 1}-
              {Math.min(currentPage * usersPerPage, sortedUsers.length)} of{" "}
              {sortedUsers.length}
            </p>
          )}
        </>
      )}

      {showEditModal && (
        <UserEditModal
          show={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          selectedUser={editingUser}
          handleUserDetailChange={handleUserDetailChange}
          handleOrganizationDetailChange={handleOrganizationDetailChange}
          handleSave={handleSave}
          setShowToast={setShowToast}
          setToastHeader={setToastHeader}
          setToastBody={setToastBody}
        />
      )}
    </div>
  );
};

export default UserManagementComponent;
